import {
  isSkillAllowedRelativePath,
  normalizeSkillRelativePath,
  SKILL_ALLOWED_EXTENSIONS,
} from "encamp-shared/src/s3/skills/paths";

/** Added to a new file name typed without an extension. */
export const DEFAULT_SKILL_FILE_EXTENSION = ".md";

/** Folder holding `path`; "" for the root. */
export function parentFolder(path: string): string {
  const slash = path.lastIndexOf("/");
  return slash === -1 ? "" : path.slice(0, slash);
}

function childPath(folder: string, name: string): string {
  return folder ? `${folder}/${name}` : name;
}

/** `folder` and every folder above it, added to `current`. */
export function withFolderExpanded(folder: string, current: ReadonlySet<string>): Set<string> {
  const next = new Set(current);
  let prefix = "";
  for (const segment of folder.split("/").filter(Boolean)) {
    prefix = childPath(prefix, segment);
    next.add(prefix);
  }
  return next;
}

/** Where `path` ends up when `from` (a file, or a folder and everything in it) moves to `to`. */
export function movedPath(path: string, from: string, to: string): string | null {
  if (path === from) {
    return to;
  }
  return path.startsWith(`${from}/`) ? `${to}${path.slice(from.length)}` : null;
}

export type SkillEntryKind = "file" | "folder";

type SkillEntryName = { path: string } | { error: string };

function withDefaultExtension(name: string): string {
  return name.includes(".") ? name : `${name}${DEFAULT_SKILL_FILE_EXTENSION}`;
}

function isTaken(path: string, paths: readonly string[], folders: readonly string[]): boolean {
  return (
    folders.includes(path) ||
    paths.some((existing) => existing === path || existing.startsWith(`${path}/`))
  );
}

/**
 * The path a typed name would create in `folder`, or why it can't. `currentPath`
 * is the entry being renamed, which may keep its own name.
 */
export function resolveSkillEntryName(params: {
  kind: SkillEntryKind;
  folder: string;
  name: string;
  paths: readonly string[];
  folders: readonly string[];
  currentPath?: string;
}): SkillEntryName {
  const name = params.name.trim();
  if (!name) {
    return { error: "Enter a name" };
  }
  if (name.includes("/")) {
    return { error: "Names can't contain /" };
  }
  const fileName = params.kind === "file" ? withDefaultExtension(name) : name;
  const path = normalizeSkillRelativePath(childPath(params.folder, fileName));
  if (!path) {
    return { error: "That name isn't allowed" };
  }
  if (params.kind === "file" && !isSkillAllowedRelativePath(path)) {
    return { error: `Use ${SKILL_ALLOWED_EXTENSIONS.join(", ")}` };
  }
  if (path !== params.currentPath && isTaken(path, params.paths, params.folders)) {
    return { error: `${fileName} already exists here` };
  }
  return { path };
}
